import type { MaybeRef } from 'vue'
import type { ISbStoryData } from '@storyblok/js'

interface StoryblokContentBase<T extends string> {
  _uid?: string
  component?: T
  _editable?: string
  simple_layout_button_text: string
  layout: 'simple' | 'default' | 'blank'
  nav_color?: { value?: string }
  nav_text_color?: '' | '#000033' | '#fff' | { value?: string }
  footer_color: {
    value: string
  }
  nav_transparent?: boolean | string
  [index: string]: any
}
type StoryblokData<T> = ISbStoryData<StoryblokContentBase<string> & T>

const isTruthyFlag = (value: unknown) =>
  value === true || value === 'true' || value === 1 || value === '1'

const extractColorValue = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim()
  }
  if (value && typeof value === 'object' && 'value' in value) {
    const inner = (value as { value?: unknown }).value
    if (typeof inner === 'string' && inner.trim()) {
      return inner.trim()
    }
  }
  return undefined
}

const normalizeButtonsColor = (value: unknown) => {
  const color = extractColorValue(value)?.toLowerCase()
  if (color === '#fff' || color === '#ffffff' || color === 'white') {
    return '#fff' as const
  }
  if (color === '#000033' || color === 'black') {
    return '#000033' as const
  }
  return undefined
}

export const useStoryLayout = <T>(storyRef: MaybeRef<StoryblokData<T>>) => {
  const { t } = useI18n()
  const story = computed(() => unref(storyRef))

  const navTransparent = computed(() =>
    isTruthyFlag(story.value?.content?.nav_transparent),
  )
  const navTextColor = computed(() =>
    normalizeButtonsColor(story.value?.content?.nav_text_color),
  )
  const layoutName = computed(() => story.value?.content?.layout || 'default')
  const layoutButtonText = computed(
    () =>
      story.value?.content?.simple_layout_button_text ||
      t('common.navigation.cta'),
  )
  const layoutNavbarColor = computed(
    () => extractColorValue(story.value?.content?.nav_color) || '#fff',
  )
  const layoutFooterColor = computed(
    () => story.value?.content?.footer_color?.value || '#fff',
  )

  return {
    layoutName,
    layoutNavbarColor,
    layoutButtonText,
    layoutFooterColor,
    navTransparent,
    navTextColor,
  }
}
